import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";

import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import {
  Button, Dropdown, Input, Space, Tabs,
} from "antd";
import debounce from "lodash/debounce";
import { toast } from "react-toastify";

import Groups from "./Groups";
import RequestNewTestModal from "./RequestNewTestModal";
import TestRequests from "./TestRequests";
import TestsBrowse, { MyTestsColumn } from "./TestsBrowse";

import { canRequestTest, hasPermission, permissions } from "../../../utils/Permissions";
import {
  isValidSampleCategory,
  PRODUCT_ENVIRONMENT_CATEGORY,
  SAMPLE_SUBMISSION_BASE,
} from "../utils";
import getTestName, {
  getGroupName, getGroupId, getGroupTests, getUnlockRequestTestName, isUnlockRequest,
  parseNextPage, sortTestsByOption, buildPaginationState,
} from "./helpers";

import {
  createCustomerGroup,
  CUSTOMER_GROUP_TYPE,
  deleteCustomerGroup,
  getCustomerGroups,
  getTestDirectory,
  getTestRequests,
  TEST_SUGGESTION_CATEGORY,
  updateCustomerGroup,
} from "../../../actions/testDirectory";

import { ReactComponent as FilterIcon } from "../../../assets/icons/Sample Submission Redesign/filter-icon.svg";
import { ReactComponent as SearchIcon } from "../../../assets/icons/Sample Submission Redesign/search-line.svg";

import "./TestDirectory.css";

const TEST_SECTIONS = [
  { key: "tests", label: "Unlock More Tests" },
  { key: "pending", label: "Pending Requests" },
  { key: "past", label: "Past Requests" },
];

const SORT_OPTIONS = [
  { key: "recent", label: "Recently Added" },
  { key: "az", label: "Alphabetical (A-Z)" },
  { key: "za", label: "Alphabetical (Z-A)" },
];

const SEARCH_DEBOUNCE_MS = 500;

/** Prefer URL/return category; else derive from create-product / create-environment permissions. */
function resolveSampleSide(preferred) {
  if (isValidSampleCategory(preferred)) return preferred;
  const canProduct = hasPermission(permissions.sampleSubmission.createProductSubmission);
  const canEnv = hasPermission(permissions.sampleSubmission.createEnvironmentSubmission);
  if (canProduct && canEnv) return PRODUCT_ENVIRONMENT_CATEGORY;
  if (canEnv && !canProduct) return "environment";
  return "product";
}

function resolveInitialTestDirectoryTab(search = "") {
  const params = new URLSearchParams(search);
  if (params.get("request-new-test") === "true") return "test-request";
  if (params.get("tab") === "groups") return "groups";
  if (params.get("tab") === "test-request") return "test-request";
  return "my-tests";
}

function resolveInitialTestRequestSection(search = "") {
  const params = new URLSearchParams(search);
  if (params.get("request-new-test") === "true" || params.get("tab") === "test-request") {
    return "pending";
  }
  return "tests";
}

export default function TestDirectory({ history, location }) {
  const sampleSide = useMemo(() => {
    const params = new URLSearchParams(location?.search || "");
    return resolveSampleSide(params.get("return-category") || "");
  }, [location]);

  const [activeTab, setActiveTab] = useState(
    () => resolveInitialTestDirectoryTab(location?.search || ""),
  );
  const [activeSection, setActiveSection] = useState(
    () => resolveInitialTestRequestSection(location?.search || ""),
  );
  const [requestsSearch, setRequestsSearch] = useState("");
  const [myTestsSearch, setMyTestsSearch] = useState("");
  const [unlockSearch, setUnlockSearch] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [isCompactLayout, setIsCompactLayout] = useState(false);
  const [customerTests, setCustomerTests] = useState([]);
  const [directoryTests, setDirectoryTests] = useState([]);
  const [groups, setGroups] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [pastRequests, setPastRequests] = useState([]);
  const [pendingRequestedNames, setPendingRequestedNames] = useState([]);
  const [pendingPagination, setPendingPagination] = useState({
    currentPage: 1, totalPages: 1, pageSize: 10, totalCount: 0, nextPageLink: "", prevPageLink: "",
  });
  const [pastPagination, setPastPagination] = useState({
    currentPage: 1, totalPages: 1, pageSize: 10, totalCount: 0, nextPageLink: "", prevPageLink: "",
  });
  const [loadingMyTests, setLoadingMyTests] = useState(
    () => resolveInitialTestDirectoryTab(location?.search || "") === "my-tests",
  );
  const [loadingUnlock, setLoadingUnlock] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(
    () => resolveInitialTestDirectoryTab(location?.search || "") === "groups",
  );
  const [loadingPending, setLoadingPending] = useState(false);
  const [loadingPast, setLoadingPast] = useState(false);
  const [loadingPendingNewContent, setLoadingPendingNewContent] = useState(false);
  const [loadingPastNewContent, setLoadingPastNewContent] = useState(false);
  const [showRequestNew, setShowRequestNew] = useState(false);
  const [requestNewPrefill, setRequestNewPrefill] = useState("");
  const [savingGroup, setSavingGroup] = useState(false);
  const isMountedRef = useRef(true);
  const myTestsSourceRef = useRef([]);
  const sortByRef = useRef(sortBy);
  const myTestsRequestIdRef = useRef(0);
  const unlockRequestIdRef = useRef(0);
  const pendingRequestIdRef = useRef(0);
  const pastRequestIdRef = useRef(0);
  const groupsRequestIdRef = useRef(0);
  const pendingNamesRequestIdRef = useRef(0);
  const unlockRequestedNamesLoadedRef = useRef(false);
  const prevActiveSectionRef = useRef("tests");
  const loadPendingRequestsRef = useRef(async () => {});
  const pendingScrollbarRef = useRef(null);
  const pastScrollbarRef = useRef(null);

  const openRequestNewTest = useCallback((prefill = "") => {
    if (!canRequestTest()) return;
    setRequestNewPrefill(prefill);
    setShowRequestNew(true);
  }, []);

  const closeRequestNewTest = useCallback(() => {
    setShowRequestNew(false);
    setRequestNewPrefill("");
    if (history && location?.search?.includes("request-new-test")) {
      history.replace(location.pathname);
    }
    if (activeTab === "test-request" && activeSection === "pending") {
      loadPendingRequestsRef.current("", 1);
    }
  }, [history, location, activeTab, activeSection]);

  const handleRequestNewDone = useCallback(() => {
    closeRequestNewTest();
  }, [closeRequestNewTest]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location?.search || "");
    if (params.get("request-new-test") === "true") {
      setActiveTab("test-request");
      setActiveSection("pending");
      openRequestNewTest(params.get("test-name") || "");
    } else if (params.get("tab") === "groups") {
      setActiveTab("groups");
    } else if (params.get("tab") === "test-request") {
      setActiveTab("test-request");
      setActiveSection("pending");
    }
  }, [location, openRequestNewTest]);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const media = window.matchMedia("(max-width: 768px)");
    const syncLayout = () => setIsCompactLayout(media.matches);
    syncLayout();
    if (media.addEventListener) {
      media.addEventListener("change", syncLayout);
      return () => media.removeEventListener("change", syncLayout);
    }
    media.addListener(syncLayout);
    return () => media.removeListener(syncLayout);
  }, []);

  const loadMyTests = useCallback(async (search = "") => {
    const requestId = myTestsRequestIdRef.current + 1;
    myTestsRequestIdRef.current = requestId;
    setLoadingMyTests(true);
    const result = await getTestDirectory(
      search,
      sampleSide,
      undefined,
      undefined,
      TEST_SUGGESTION_CATEGORY.CUSTOMER_MAPPING,
    );
    if (!isMountedRef.current || requestId !== myTestsRequestIdRef.current) return;
    if (result.success) {
      myTestsSourceRef.current = result.tests || [];
      setCustomerTests(sortTestsByOption(myTestsSourceRef.current, sortByRef.current));
    } else {
      toast.error(result.message || "Unable to load tests.");
      myTestsSourceRef.current = [];
      setCustomerTests([]);
    }
    setLoadingMyTests(false);
  }, [sampleSide]);

  const mergeUnlockRequestedNames = useCallback((requests) => {
    const names = (Array.isArray(requests) ? requests : []).flatMap((item) => {
      if (typeof item === "string") return [item];
      if (isUnlockRequest(item)) return [getUnlockRequestTestName(item)];
      const name = getTestName(item);
      return name ? [name] : [];
    }).filter(Boolean);
    if (!names.length) return;
    setPendingRequestedNames((current) => {
      const byLower = new Map();
      [...current, ...names].forEach((name) => {
        if (name) byLower.set(name.toLowerCase(), name);
      });
      return Array.from(byLower.values());
    });
  }, []);

  const loadUnlockTests = useCallback(async (search = "") => {
    const requestId = unlockRequestIdRef.current + 1;
    unlockRequestIdRef.current = requestId;
    setLoadingUnlock(true);
    const result = await getTestDirectory(
      search,
      sampleSide,
      undefined,
      undefined,
      TEST_SUGGESTION_CATEGORY.TEST_DIRECTORY,
    );
    if (!isMountedRef.current || requestId !== unlockRequestIdRef.current) return;
    if (result.success) {
      const directoryList = result.directoryTests?.length
        ? result.directoryTests
        : (result.tests || []);
      const optionalRequested = (result.requestedDirectoryTests || [])
        .map((test) => (typeof test === "string" ? test : getUnlockRequestTestName(test) || getTestName(test)))
        .filter(Boolean);

      setDirectoryTests(sortTestsByOption(directoryList, "recent"));
      if (optionalRequested.length) {
        mergeUnlockRequestedNames(optionalRequested);
      }
    } else {
      toast.error(result.message || "Unable to load unlock tests.");
      setDirectoryTests([]);
    }
    setLoadingUnlock(false);
  }, [sampleSide, mergeUnlockRequestedNames]);

  /** Paginate pending /testrequest/ to derive unlock (Requested) labels — FE-only, no BE is_requested. */
  const refreshUnlockRequestedNames = useCallback(async () => {
    const requestId = pendingNamesRequestIdRef.current + 1;
    pendingNamesRequestIdRef.current = requestId;
    let page = 1;
    let names = [];
    let guard = 0;
    while (guard < 50) {
      guard += 1;
      // eslint-disable-next-line no-await-in-loop
      const result = await getTestRequests("pending", "", page);
      if (!isMountedRef.current || requestId !== pendingNamesRequestIdRef.current) return;
      if (!result.success) break;
      names = names.concat(
        (result.requests || [])
          .filter(isUnlockRequest)
          .map(getUnlockRequestTestName)
          .filter(Boolean),
      );
      const isLast = !result.nextPageLink
        || parseInt(result.currentPage, 10) >= parseInt(result.totalPages, 10);
      if (isLast) break;
      page = Number(parseNextPage(result.nextPageLink, page)) || page + 1;
    }
    if (!isMountedRef.current || requestId !== pendingNamesRequestIdRef.current) return;
    setPendingRequestedNames(names);
    unlockRequestedNamesLoadedRef.current = true;
  }, []);

  const shouldRefreshUnlockRequestedNames = useCallback((previousSection) => (
    !unlockRequestedNamesLoadedRef.current
    || previousSection === "pending"
    || previousSection === "past"
  ), []);

  const loadGroups = useCallback(async (search = "") => {
    const requestId = groupsRequestIdRef.current + 1;
    groupsRequestIdRef.current = requestId;
    setLoadingGroups(true);
    const { success, groups: nextGroups, message } = await getCustomerGroups(search);
    if (!isMountedRef.current || requestId !== groupsRequestIdRef.current) return;
    setGroups(success ? nextGroups : []);
    if (!success) {
      toast.error(message || "Unable to load groups.");
    }
    setLoadingGroups(false);
  }, []);

  const applyPendingResult = useCallback((result, page, append = false) => {
    const nextRequests = result.success ? (result.requests || []) : [];
    setPendingRequests((current) => (
      append && Number(page) > 1 ? [...current, ...nextRequests] : nextRequests
    ));
    setPendingPagination(buildPaginationState(result, page));
  }, []);

  const applyPastResult = useCallback((result, page, append = false) => {
    const nextRequests = result.success ? (result.requests || []) : [];
    setPastRequests((current) => (
      append && Number(page) > 1 ? [...current, ...nextRequests] : nextRequests
    ));
    setPastPagination(buildPaginationState(result, page));
  }, []);

  const cancelPendingAndPastLoads = useCallback(() => {
    pendingRequestIdRef.current += 1;
    pastRequestIdRef.current += 1;
    setLoadingPending(false);
    setLoadingPendingNewContent(false);
    setLoadingPast(false);
    setLoadingPastNewContent(false);
  }, []);

  const loadPendingRequests = useCallback(async (search = "", page = 1) => {
    const requestId = pendingRequestIdRef.current + 1;
    pendingRequestIdRef.current = requestId;
    const isLoadMore = Number(page) > 1;
    if (isLoadMore) {
      setLoadingPendingNewContent(true);
    } else {
      setLoadingPending(true);
    }
    const result = await getTestRequests("pending", search, page);
    if (!isMountedRef.current || requestId !== pendingRequestIdRef.current) {
      if (isLoadMore) setLoadingPendingNewContent(false);
      else setLoadingPending(false);
      return;
    }
    applyPendingResult(result, page, isLoadMore);
    if (result.success) {
      mergeUnlockRequestedNames(result.requests);
    }
    if (!result.success) {
      toast.error(result.message || "Unable to load pending requests.");
      if (isLoadMore && pendingScrollbarRef.current) {
        pendingScrollbarRef.current.scrollTop -= 100;
      } else if (!isLoadMore) {
        setPendingRequests([]);
      }
    }
    if (isLoadMore) {
      if (pendingScrollbarRef.current) {
        pendingScrollbarRef.current.scrollTop -= 4;
      }
      setLoadingPendingNewContent(false);
    } else {
      setLoadingPending(false);
      if (pendingScrollbarRef.current) {
        pendingScrollbarRef.current.scrollToTop();
      }
    }
  }, [applyPendingResult, mergeUnlockRequestedNames]);

  const loadPastRequests = useCallback(async (search = "", page = 1) => {
    const requestId = pastRequestIdRef.current + 1;
    pastRequestIdRef.current = requestId;
    const isLoadMore = Number(page) > 1;
    if (isLoadMore) {
      setLoadingPastNewContent(true);
    } else {
      setLoadingPast(true);
    }
    const result = await getTestRequests("history", search, page);
    if (!isMountedRef.current || requestId !== pastRequestIdRef.current) {
      if (isLoadMore) setLoadingPastNewContent(false);
      else setLoadingPast(false);
      return;
    }
    applyPastResult(result, page, isLoadMore);
    if (!result.success) {
      toast.error(result.message || "Unable to load past requests.");
      if (isLoadMore && pastScrollbarRef.current) {
        pastScrollbarRef.current.scrollTop -= 100;
      } else if (!isLoadMore) {
        setPastRequests([]);
      }
    }
    if (isLoadMore) {
      if (pastScrollbarRef.current) {
        pastScrollbarRef.current.scrollTop -= 4;
      }
      setLoadingPastNewContent(false);
    } else {
      setLoadingPast(false);
      if (pastScrollbarRef.current) {
        pastScrollbarRef.current.scrollToTop();
      }
    }
  }, [applyPastResult]);

  loadPendingRequestsRef.current = loadPendingRequests;

  /** Load the visible Test Requests section when its tab/section opens (incl. deep link from Sample Submission). */
  useEffect(() => {
    if (activeTab !== "test-request") return undefined;

    const previousSection = prevActiveSectionRef.current;

    if (activeSection === "tests") {
      loadUnlockTests(unlockSearch);
      if (shouldRefreshUnlockRequestedNames(previousSection)) {
        refreshUnlockRequestedNames();
      }
    } else if (activeSection === "pending") {
      loadPendingRequests(requestsSearch, 1);
    } else if (activeSection === "past") {
      loadPastRequests(requestsSearch, 1);
    }

    prevActiveSectionRef.current = activeSection;
    return undefined;
    // Search within a section is handled by debounced handlers; reload only when the section opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeSection]);

  const handlePendingLoadMore = useCallback((page) => {
    loadPendingRequests(requestsSearch, page);
  }, [loadPendingRequests, requestsSearch]);

  const handlePastLoadMore = useCallback((page) => {
    loadPastRequests(requestsSearch, page);
  }, [loadPastRequests, requestsSearch]);

  useEffect(() => {
    if (loadingPendingNewContent && pendingScrollbarRef.current) {
      pendingScrollbarRef.current.scrollToBottom();
    }
  }, [loadingPendingNewContent]);

  useEffect(() => {
    if (loadingPastNewContent && pastScrollbarRef.current) {
      pastScrollbarRef.current.scrollToBottom();
    }
  }, [loadingPastNewContent]);

  const debouncedLoadMyTests = useMemo(
    () => debounce((search) => {
      loadMyTests(search);
    }, SEARCH_DEBOUNCE_MS),
    [loadMyTests],
  );

  const debouncedLoadUnlockTests = useMemo(
    () => debounce((search) => {
      loadUnlockTests(search);
    }, SEARCH_DEBOUNCE_MS),
    [loadUnlockTests],
  );

  const debouncedLoadPendingRequests = useMemo(
    () => debounce((search) => {
      loadPendingRequests(search, 1);
    }, SEARCH_DEBOUNCE_MS),
    [loadPendingRequests],
  );

  const debouncedLoadPastRequests = useMemo(
    () => debounce((search) => {
      loadPastRequests(search, 1);
    }, SEARCH_DEBOUNCE_MS),
    [loadPastRequests],
  );

  useEffect(() => () => {
    debouncedLoadMyTests.cancel();
    debouncedLoadUnlockTests.cancel();
    debouncedLoadPendingRequests.cancel();
    debouncedLoadPastRequests.cancel();
  }, [
    debouncedLoadMyTests,
    debouncedLoadUnlockTests,
    debouncedLoadPendingRequests,
    debouncedLoadPastRequests,
  ]);

  /** Refetch My Tests or Groups when their main tab opens (Drafts schedule-tab pattern). */
  useEffect(() => {
    if (activeTab === "my-tests") {
      loadMyTests(myTestsSearch);
    } else if (activeTab === "groups") {
      loadGroups();
    }
    return undefined;
    // Search within My Tests uses dedicated handlers; reload on tab open only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  useEffect(() => {
    if (activeTab !== "my-tests") return undefined;
    setCustomerTests(sortTestsByOption(myTestsSourceRef.current, sortBy));
    return undefined;
  }, [sortBy, activeTab]);

  const handleMyTestsSearch = useCallback((value) => {
    setMyTestsSearch(value);
    debouncedLoadMyTests(value);
  }, [debouncedLoadMyTests]);

  /** Immediate search on Enter — same pattern as AnalysisRequest handleHitEnter. */
  const handleMyTestsSearchEnter = useCallback((value) => {
    debouncedLoadMyTests.cancel();
    setMyTestsSearch(value);
    loadMyTests(value);
  }, [debouncedLoadMyTests, loadMyTests]);

  const handleUnlockSearch = useCallback((value) => {
    setUnlockSearch(value);
    debouncedLoadUnlockTests(value);
  }, [debouncedLoadUnlockTests]);

  const handleUnlockSearchEnter = useCallback((value) => {
    debouncedLoadUnlockTests.cancel();
    setUnlockSearch(value);
    loadUnlockTests(value);
  }, [debouncedLoadUnlockTests, loadUnlockTests]);

  /** Search only the visible requests section (same idea as My Tests vs Unlock search). */
  const handleRequestsSearch = useCallback((value) => {
    setRequestsSearch(value);
    if (activeSection === "past") {
      debouncedLoadPastRequests(value);
      return;
    }
    if (activeSection === "pending") {
      debouncedLoadPendingRequests(value);
    }
  }, [activeSection, debouncedLoadPendingRequests, debouncedLoadPastRequests]);

  const handleRequestsSearchEnter = useCallback((value) => {
    debouncedLoadPendingRequests.cancel();
    debouncedLoadPastRequests.cancel();
    setRequestsSearch(value);
    if (activeSection === "past") {
      loadPastRequests(value, 1);
      return;
    }
    if (activeSection === "pending") {
      loadPendingRequests(value, 1);
    }
  }, [
    activeSection,
    debouncedLoadPendingRequests,
    debouncedLoadPastRequests,
    loadPastRequests,
    loadPendingRequests,
  ]);

  const handleTabChange = (tabKey) => {
    debouncedLoadPendingRequests.cancel();
    debouncedLoadPastRequests.cancel();
    debouncedLoadUnlockTests.cancel();

    if (requestsSearch) {
      setRequestsSearch("");
    }

    if (tabKey === "test-request") {
      cancelPendingAndPastLoads();
      setActiveTab(tabKey);
      setActiveSection("tests");
      return;
    }

    setActiveTab(tabKey);
  };

  /**
   * Refetch the opened section when switching (Drafts schedule-tab pattern).
   * Clears shared search so the other section is not left with a stale filter.
   */
  const handleSectionChange = (sectionKey) => {
    if (sectionKey === activeSection) return;

    debouncedLoadPendingRequests.cancel();
    debouncedLoadPastRequests.cancel();
    debouncedLoadUnlockTests.cancel();
    cancelPendingAndPastLoads();

    if (activeSection === "pending" && sectionKey === "tests") {
      mergeUnlockRequestedNames(pendingRequests);
    }

    setActiveSection(sectionKey);
    if (requestsSearch) {
      setRequestsSearch("");
    }
  };

  const requestedNames = useMemo(() => {
    const byLower = new Map();
    pendingRequestedNames.forEach((name) => {
      if (name) byLower.set(String(name).toLowerCase(), name);
    });
    pendingRequests
      .filter(isUnlockRequest)
      .map(getUnlockRequestTestName)
      .filter(Boolean)
      .forEach((name) => {
        byLower.set(String(name).toLowerCase(), name);
      });
    return Array.from(byLower.values());
  }, [pendingRequestedNames, pendingRequests]);

  const handleCreateGroup = async ({ group_name, test_list }) => {
    setSavingGroup(true);
    const { success, message } = await createCustomerGroup({
      group_name,
      test_list,
      group_type: CUSTOMER_GROUP_TYPE,
    });
    if (!isMountedRef.current) return false;
    setSavingGroup(false);
    if (success) {
      toast.success(message || "Group created successfully.");
      loadGroups();
      return true;
    }

    toast.error(message || "Unable to create group.");
    return false;
  };

  const handleRenameGroup = async (group, nextName) => {
    const currentName = getGroupName(group);
    const groupId = getGroupId(group);
    const test_list = getGroupTests(group).map((t) => (typeof t === "string" ? t : (t?.name || t?.test_name || ""))).filter(Boolean);
    setSavingGroup(true);
    const { success, message } = await updateCustomerGroup({
      group_id: groupId,
      prev_group: groupId ? undefined : currentName,
      group_name: nextName,
      test_list,
      group_type: group.group_type || group.type || CUSTOMER_GROUP_TYPE,
    });
    if (!isMountedRef.current) return false;
    setSavingGroup(false);
    if (success) {
      toast.success(message || "Group renamed.");
      loadGroups();
      return true;
    }

    toast.error(message || "Unable to rename group.");
    return false;
  };

  const handleDeleteGroup = async (group) => {
    const currentName = getGroupName(group);
    const groupId = getGroupId(group);
    setSavingGroup(true);
    const { success, message } = await deleteCustomerGroup({
      group_id: groupId,
      group_name: groupId ? undefined : currentName,
      group_type: group.group_type || group.type || CUSTOMER_GROUP_TYPE,
    });
    if (!isMountedRef.current) return;
    setSavingGroup(false);
    if (success) {
      toast.success(message || "Group deleted.");
      loadGroups();
      return;
    }

    toast.error(message || "Unable to delete group.");
  };

  const handleUpdateGroupTests = async (group, test_list) => {
    const currentName = getGroupName(group);
    const groupId = getGroupId(group);
    const normalizedList = (Array.isArray(test_list) ? test_list : [])
      .map((t) => (typeof t === "string" ? t : (t?.name || t?.test_name || "")))
      .filter(Boolean);
    const { success, message } = await updateCustomerGroup({
      group_id: groupId,
      prev_group: groupId ? undefined : currentName,
      group_name: currentName,
      test_list: normalizedList,
      group_type: group.group_type || group.type || CUSTOMER_GROUP_TYPE,
    });
    if (!isMountedRef.current) return false;
    if (success) {
      setGroups((current) => current.map((item) => {
        const itemId = getGroupId(item);
        if (groupId && itemId) {
          if (itemId !== groupId) return item;
        } else {
          const itemName = getGroupName(item);
          if (item !== group && itemName !== currentName) return item;
        }
        return {
          ...item,
          ...(groupId ? { group_id: groupId } : {}),
          test_list: normalizedList,
          tests: normalizedList,
        };
      }));
      return true;
    }

    toast.error(message || "Unable to update group tests.");
    return false;
  };

  const handleRequestsSubmitted = useCallback((submittedRequests, { goToPending = true } = {}) => {
    const requests = Array.isArray(submittedRequests) ? submittedRequests : [submittedRequests];
    const requestDate = new Date().toLocaleDateString();
    const newPendingRequests = requests.map((request) => {
      const testName = request.test_name || request.requested_name || "";
      return {
        ...request,
        test_name: testName,
        requested_name: request.requested_name || testName,
        request_date: requestDate,
        status: "pending",
      };
    });
    setPendingRequests((current) => {
      const submittedNames = new Set(
        newPendingRequests.map(({ test_name = "" }) => test_name.toLowerCase()).filter(Boolean),
      );
      return [
        ...newPendingRequests,
        ...current.filter(({ test_name = "" }) => !submittedNames.has(test_name.toLowerCase())),
      ];
    });
    setPendingRequestedNames((current) => {
      const unlockRequests = newPendingRequests.filter(isUnlockRequest);
      if (!unlockRequests.length) return current;
      const byLower = new Map();
      [...current, ...unlockRequests.map(getUnlockRequestTestName)].forEach((name) => {
        if (name) byLower.set(name.toLowerCase(), name);
      });
      unlockRequestedNamesLoadedRef.current = true;
      return Array.from(byLower.values());
    });
    if (goToPending) {
      setActiveTab("test-request");
      setActiveSection("pending");
      setRequestsSearch("");
      loadPendingRequests("", 1);
    }
  }, [loadPendingRequests]);

  const goBack = () => {
    if (history) history.push(SAMPLE_SUBMISSION_BASE);
  };

  const renderLoading = () => (
    <div className="TestDirectory__Loading" aria-busy="true">
      <div className="TestDirectory__Skeleton ActiveloadingAnimation" />
    </div>
  );

  const sortControl = (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      overlayClassName="TestDirectory__SortDropdown"
      menu={{
        selectable: true,
        selectedKeys: [sortBy],
        onClick: ({ key }) => setSortBy(key),
        items: SORT_OPTIONS.map((option) => ({
          key: option.key,
          label: option.label,
        })),
      }}
    >
      <button
        type="button"
        className={[
          "TestDirectory__FilterBtn",
          sortBy !== "recent" ? "TestDirectory__FilterBtn--active" : "",
        ].filter(Boolean).join(" ")}
        aria-label="Sort and filter tests"
      >
        <FilterIcon aria-hidden />
      </button>
    </Dropdown>
  );

  const mainTabItems = [
    {
      key: "my-tests",
      label: "My Tests",
      children: (
        <div className="TestDirectory__Pane TestDirectory__Pane--myTests">
          <MyTestsColumn
            customerTests={customerTests}
            search={myTestsSearch}
            onSearch={handleMyTestsSearch}
            onSearchEnter={handleMyTestsSearchEnter}
            loading={loadingMyTests}
            toolbarExtra={sortControl}
          />
        </div>
      ),
    },
    {
      key: "test-request",
      label: "Test Requests",
      children: (
        <div className="TestDirectory__Pane TestDirectory__Pane--testRequests">
          <div className="TestDirectory__MyTestsWorkspace">
            <div className="TestDirectory__SectionToolbar">
              <div
                className="TestDirectory__SectionTabs"
                role="tablist"
                aria-label="Test Requests sections"
              >
                {TEST_SECTIONS.map((section) => (
                  <button
                    key={section.key}
                    type="button"
                    role="tab"
                    aria-selected={activeSection === section.key}
                    className={`TestDirectory__SectionTab ${activeSection === section.key ? "TestDirectory__SectionTab--active" : ""}`}
                    onClick={() => handleSectionChange(section.key)}
                  >
                    {section.label}
                  </button>
                ))}
              </div>

              <div className="TestDirectory__SearchWrap">
                <Input
                  allowClear
                  value={activeSection === "tests" ? unlockSearch : requestsSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (activeSection === "tests") handleUnlockSearch(value);
                    else handleRequestsSearch(value);
                  }}
                  onClear={() => {
                    if (activeSection === "tests") handleUnlockSearch("");
                    else handleRequestsSearch("");
                  }}
                  onPressEnter={(e) => {
                    const value = e.target.value;
                    if (activeSection === "tests") handleUnlockSearchEnter(value);
                    else handleRequestsSearchEnter(value);
                  }}
                  placeholder="Search Tests"
                  className="TestDirectory__SearchInput TestDirectory__MyTestsSearch"
                  prefix={<SearchIcon aria-hidden className="TestDirectory__SearchIcon" />}
                />
              </div>
            </div>

            <div className="TestDirectory__SectionView">
              {activeSection === "tests" && (
                <TestsBrowse
                  directoryTests={directoryTests}
                  requestedNames={requestedNames}
                  unlockSearch={unlockSearch}
                  loadingUnlock={loadingUnlock}
                  onUnlockSubmitted={(requests) => handleRequestsSubmitted(
                    requests.map((request) => ({
                      ...request,
                      type: request.type || "unlock",
                      request_type: request.type || request.request_type || "unlock",
                    })),
                    { goToPending: false },
                  )}
                />
              )}
              {activeSection === "pending" && (
                loadingPending
                  ? renderLoading()
                  : (
                    <TestRequests
                      requests={pendingRequests}
                      search={requestsSearch}
                      paginationInfo={pendingPagination}
                      loading={loadingPending}
                      loadingNewContent={loadingPendingNewContent}
                      onLoadMore={handlePendingLoadMore}
                      scrollbarRef={pendingScrollbarRef}
                    />
                  )
              )}
              {activeSection === "past" && (
                loadingPast
                  ? renderLoading()
                  : (
                    <TestRequests
                      requests={pastRequests}
                      search={requestsSearch}
                      showResolution
                      paginationInfo={pastPagination}
                      loading={loadingPast}
                      loadingNewContent={loadingPastNewContent}
                      onLoadMore={handlePastLoadMore}
                      scrollbarRef={pastScrollbarRef}
                    />
                  )
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "groups",
      label: "Groups",
      children: (
        <div className="TestDirectory__Pane TestDirectory__Pane--groups">
          {loadingGroups ? (
            renderLoading()
          ) : (
            <Groups
              groups={groups}
              saving={savingGroup}
              sampleCategory={sampleSide}
              onCreate={handleCreateGroup}
              onRename={handleRenameGroup}
              onDelete={handleDeleteGroup}
              onUpdateTests={handleUpdateGroupTests}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="sample-submission-background main TestDirectoryPage">
      <nav className="TestDirectory__Breadcrumb" aria-label="Breadcrumb">
        <div className="TestDirectory__BreadcrumbInner">
          <div className="TestDirectory__BreadcrumbNavIcons">
            <button
              type="button"
              className="TestDirectory__BreadcrumbNavBtn"
              onClick={goBack}
              aria-label="Back to Sample Submission"
            >
              <LeftOutlined aria-hidden className="TestDirectory__BreadcrumbNavIcon" />
            </button>
            <button
              type="button"
              className="TestDirectory__BreadcrumbNavBtn TestDirectory__BreadcrumbNavBtn--disabled"
              disabled
              aria-disabled="true"
              aria-label="Forward"
              tabIndex={-1}
            >
              <RightOutlined aria-hidden className="TestDirectory__BreadcrumbNavIcon" />
            </button>
          </div>
          <div className="TestDirectory__BreadcrumbTrail">
            <span className="TestDirectory__BreadcrumbItem TestDirectory__BreadcrumbItem--ancestor">
              Sample Submission
            </span>
            <span className="TestDirectory__BreadcrumbDivider" aria-hidden>
              /
            </span>
            <span className="TestDirectory__BreadcrumbItem TestDirectory__BreadcrumbItem--current">
              Test Directory
            </span>
          </div>
        </div>
      </nav>

      <div className="TestDirectoryPage__Content">
        <div className={`TestDirectory sample-submission-main-container${isCompactLayout ? " TestDirectory--compact" : ""}`}>
          <div className="TestDirectory__Header">
            <Space size={12} className="TestDirectory__TitleGroup">
              <button
                type="button"
                className="TestDirectory__Back"
                onClick={goBack}
                aria-label="Back to Sample Submission"
              >
                <LeftOutlined />
              </button>
              <h1 className="TestDirectory__Title">Test Directory</h1>
            </Space>
            {canRequestTest() && (
              <Button
                type="primary"
                className="TestDirectory__RequestNewBtn"
                htmlType="button"
                onClick={() => openRequestNewTest()}
              >
                {isCompactLayout ? "Request New" : "Request New Test"}
              </Button>
            )}
          </div>

          <Tabs
            tabPosition={isCompactLayout ? "top" : "left"}
            activeKey={activeTab}
            onChange={handleTabChange}
            destroyOnHidden
            className="TestDirectory__MainTabs"
            items={mainTabItems}
          />
        </div>
      </div>

      {showRequestNew && (
        <RequestNewTestModal
          initialTestName={requestNewPrefill}
          onCancel={closeRequestNewTest}
          onDone={handleRequestNewDone}
          onSubmitted={(request) => handleRequestsSubmitted({
            ...request,
            type: request.type || "new",
            request_type: request.type || request.request_type || "new",
          })}
        />
      )}
    </div>
  );
}
